import { useState, useEffect, useRef, useCallback, type MouseEvent } from 'react';
import { useNavigate, useNavigationType, useParams, useSearchParams, useLocation, Link } from 'react-router';
import { Play, Pause, Download, ArrowLeft, MessageSquare, ExternalLink } from 'lucide-react';
import styled from '@emotion/styled';
import {
  PageHeader, PageTitle, Card, Table, Thead, Tbody,
  Badge, Input, Muted,
} from '../styled-components';
import { EmptyState, SkeletonRows, CursorPagination, DirectionFilter, type DirectionFilterValue, MultiSelectFilter, ResourceNotFound } from '../components';
import { useFetchData } from '../hooks';
import { canViewMonitor } from '../monitor-access';
import { CallInfo } from '@guava-ai/guava-sdk/src/socket/call-info.ts';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Conversation {
  id: string; call_id: string; ts: string | null;
  direction: string; from_number: string; to_number: string; call_info: unknown;
  duration_sec: number | null; campaign_id: string | null;
}

interface TerminationInfo {
  termination_reason: string;
  transfer_target?: string;
}

interface ConversationDetail extends Conversation {
  termination?: TerminationInfo | null;
}

interface TranscriptTurn {
  speaker: string; text: string; offset_ms: number;
}

interface ConversationListItem extends Conversation {
  snippet?: string | null;
  attempt_status?: string | null;
}

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

function formatDuration(sec: number | null): string {
  if (sec == null) return '—';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function formatTs(ts: string | null): string {
  if (!ts) return '—';
  return new Date(ts).toLocaleString('en-US', {
    month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

function formatPhone(number: string): string {
  if (!number) return '—';
  const digits = number.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return number; // international or unknown — show as-is
}

function formatFromTo(conv: Conversation): { from: string; to: string } {
  const parsed = CallInfo.safeParse(conv.call_info);
  if (parsed.success && parsed.data.call_type === 'sip') {
    return { from: parsed.data.fromUri, to: parsed.data.sip_code || '—' };
  }
  if (parsed.success && parsed.data.call_type === 'webrtc') {
    return { from: '—', to: parsed.data.webrtc_code };
  }
  return { from: formatPhone(conv.from_number), to: formatPhone(conv.to_number) };
}

// ---------------------------------------------------------------------------
// Conversations list
// ---------------------------------------------------------------------------

const OUTCOME_OPTIONS = [
  { value: 'reached', label: 'Reached' },
  { value: 'unreached', label: 'Unreached' },
  { value: 'failed', label: 'Failed' },
];

const OUTCOME_TO_STATUSES: Record<string, string[]> = {
  reached: ['completed', 'agent_hangup', 'user_hangup', 'early_hangup', 'transfer'],
  unreached: ['unreached', 'voicemail'],
  failed: ['failed'],
};

const STATUS_TO_OUTCOME: Record<string, string> = {};
for (const [outcome, statuses] of Object.entries(OUTCOME_TO_STATUSES)) {
  for (const s of statuses) STATUS_TO_OUTCOME[s] = outcome;
}

function formatOutcome(status: string): string {
  const found = OUTCOME_OPTIONS.find((o) => o.value === (STATUS_TO_OUTCOME[status] ?? status));
  return found ? found.label : status;
}

function outcomeBadgeVariant(status: string): string {
  switch (STATUS_TO_OUTCOME[status]) {
    case 'reached': return 'active';
    case 'unreached': return 'paused';
    case 'failed': return 'error';
    default: return 'default';
  }
}

const FilterBar = styled.div({
  display: 'flex',
  gap: 10,
  marginBottom: 16,
  flexWrap: 'wrap',
});

const FilterInput = styled(Input)({ width: 160 });

// The whole conversation row is clickable. The date/time cell holds a single
// real link — keyboard-focusable, right-clickable, cmd/middle-clickable to open
// in a new tab — and clicks elsewhere on the row are delegated to it by the
// <tr> onClick/onAuxClick handlers below. One real link per row keeps the
// screen-reader link list clean (one labelled destination per row) and uses no
// table-element positioning, so it works in every browser — unlike the previous
// absolutely-positioned overlay anchor, which relied on `position: relative` on
// the <tr> (undefined per spec, ignored by older Safari, so every overlay
// escaped and the bottom row swallowed all clicks).
const CellLink = styled(Link)({
  display: 'flex',
  alignItems: 'center',
  height: 'var(--table-row-height)',
  padding: '0 16px',
  color: 'inherit',
  textDecoration: 'none',
  font: 'inherit',
  boxSizing: 'border-box',
});

// SIP URIs can be long, so truncate them. '&&' outranks Tbody's `& td` font size.
const EndpointCell = styled.td({
  '&&': {
    fontFamily: 'JetBrains Mono, monospace',
    fontSize: 12,
    maxWidth: 200,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});

function setOrDelete(params: URLSearchParams, key: string, value: string) {
  if (value) params.set(key, value);
  else params.delete(key);
}

// ---------------------------------------------------------------------------
// "Just-viewed" tracking — remembers the last call you opened so the list can
// highlight it and scroll to it when you come back. Kept in sessionStorage
// (per-tab, cleared on close): this is ephemeral UI state, deliberately NOT in
// the URL so it never leaks into shared or bookmarked filter links.
// ---------------------------------------------------------------------------
const LAST_VIEWED_KEY = 'conversations:lastViewed';

function readLastViewed(): string | null {
  try { return sessionStorage.getItem(LAST_VIEWED_KEY); } catch { return null; }
}

function recordViewed(e: MouseEvent, callId: string) {
  // Only track plain same-tab navigations. A cmd/ctrl/shift-click opens the call
  // in a new tab and leaves this list in place, so highlighting or scrolling here
  // would be wrong. Middle-click fires 'auxclick', not 'click', so it never lands
  // here in the first place.
  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
  try {
    sessionStorage.setItem(LAST_VIEWED_KEY, callId);
  } catch { /* sessionStorage unavailable (e.g. private mode) — best-effort */ }
}

interface ListProps {
  campaignId?: string;
  onSelect?: (callId: string) => void;
}

function ConversationsList({ campaignId }: ListProps) {
  // The list's filters live in the URL query string (not local state) so that
  // opening a conversation and hitting Back restores them, and so the view is
  // bookmarkable / shareable. Pages are opaque next_urls from the API, so the
  // stack of visited page URLs lives in history state instead, which Back also
  // restores. An empty stack means the first page.
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const pageUrls: string[] = (location.state as { pageUrls?: string[] } | null)?.pageUrls ?? [];
  const direction = searchParams.get('direction') || 'all';
  const dateFrom = searchParams.get('date_from') || '';
  const dateTo = searchParams.get('date_to') || '';
  const qParam = searchParams.get('q') || '';
  const fromParam = searchParams.get('from_number') || '';
  const toParam = searchParams.get('to_number') || '';
  // Outcome filter (campaign view). master kept this in local state; we fold it
  // into the URL like the other filters so it's shareable and survives Back.
  const outcomes = new Set(searchParams.getAll('outcome'));

  // Text filters keep local state for snappy typing, then debounce into the URL.
  const [search, setSearch] = useState(qParam);
  const [fromNumber, setFromNumber] = useState(fromParam);
  const [toNumber, setToNumber] = useState(toParam);

  // Keep local state in sync when the URL params change without a remount (e.g.
  // a same-route link to /conversations?from_number=415). Without this, the
  // debounce effect below would treat the stale local value as a user edit and
  // write it back, reverting the navigation. Syncing makes local === param, so
  // the debounce guard then writes nothing.
  useEffect(() => { setSearch(qParam); }, [qParam]);
  useEffect(() => { setFromNumber(fromParam); }, [fromParam]);
  useEffect(() => { setToNumber(toParam); }, [toParam]);

  // Changing filters drops the history state, which resets to the first page.
  const updateParams = useCallback(
    (mutate: (p: URLSearchParams) => void) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          mutate(next);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setPageUrls = (urls: string[]) =>
    navigate({ search: location.search }, { replace: true, state: { pageUrls: urls } });

  useEffect(() => {
    const t = setTimeout(() => {
      // Only write (and reset to the first page) when a value actually changed, so the
      // initial mount after a Back navigation doesn't clobber the restored page.
      if (search === qParam && fromNumber === fromParam && toNumber === toParam) return;
      updateParams((p) => {
        setOrDelete(p, 'q', search);
        setOrDelete(p, 'from_number', fromNumber);
        setOrDelete(p, 'to_number', toNumber);
      });
    }, 300);
    return () => clearTimeout(t);
  }, [search, fromNumber, toNumber, qParam, fromParam, toParam, updateParams]);

  const params = new URLSearchParams({ limit: '50' });
  if (direction !== 'all') params.set('direction', direction);
  if (dateFrom) params.set('date_from', dateFrom);
  if (dateTo) params.set('date_to', dateTo);
  if (qParam) params.set('q', qParam);
  if (fromParam) params.set('from_number', fromParam);
  if (toParam) params.set('to_number', toParam);
  if (campaignId) params.set('campaign_id', campaignId);
  for (const o of outcomes) {
    for (const s of (OUTCOME_TO_STATUSES[o] ?? [])) params.append('attempt_status', s);
  }

  const baseUrl = campaignId
    ? `/dashboard/api/campaigns/${campaignId}/conversations`
    : '/dashboard/api/conversations';

  const { data, loading } = useFetchData<{ results: ConversationListItem[]; next_url: string | null }>(
    pageUrls.at(-1) ?? `${baseUrl}?${params}`,
    { results: [], next_url: null },
  );

  // The call we most recently opened — highlight it and scroll it into view when
  // the reviewer comes *back* to the list. Only resume this on a POP navigation
  // (browser/dashboard Back or forward); a fresh PUSH — e.g. clicking the
  // "Conversations" nav link — starts clean, with no stale highlight or surprise
  // scroll. Captured once per mount (the list remounts on navigation).
  const navigationType = useNavigationType();
  const [lastViewed] = useState(() => (navigationType === 'POP' ? readLastViewed() : null));
  const lastViewedRowRef = useRef<HTMLTableRowElement>(null);
  const didScrollRef = useRef(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: `loading` is an intentional trigger so the scroll runs once rows have rendered and the ref exists, not a value the effect reads.
  useEffect(() => {
    // Scroll only once, the first time the row appears after returning — so later
    // filter changes on the same list don't yank the scroll position back.
    if (!didScrollRef.current && lastViewed && lastViewedRowRef.current) {
      didScrollRef.current = true;
      lastViewedRowRef.current.scrollIntoView({ block: 'center' });
    }
  }, [lastViewed, loading]);

  return (
    <div>
      <FilterBar>
        <Input
          placeholder="Search transcripts…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 220 }}
        />
        <DirectionFilter
          value={direction as DirectionFilterValue}
          onChange={(d) => updateParams((p) => setOrDelete(p, 'direction', d === 'all' ? '' : d))}
        />
        {campaignId && (
          <MultiSelectFilter
            label="Outcome"
            options={OUTCOME_OPTIONS}
            selected={outcomes}
            onChange={(s) => updateParams((p) => {
              p.delete('outcome');
              for (const o of s) p.append('outcome', o);
            })}
          />
        )}
        <FilterInput placeholder="From #" value={fromNumber} onChange={(e) => setFromNumber(e.target.value)} />
        <FilterInput placeholder="To #" value={toNumber} onChange={(e) => setToNumber(e.target.value)} />
        <FilterInput type="date" value={dateFrom} onChange={(e) => updateParams((p) => setOrDelete(p, 'date_from', e.target.value))} />
        <FilterInput type="date" value={dateTo} onChange={(e) => updateParams((p) => setOrDelete(p, 'date_to', e.target.value))} />
      </FilterBar>

      <Card style={{ padding: 0, overflow: 'hidden' }}>
        <Table>
          <Thead>
            <tr>
              <th>Date / Time</th>
              <th>Direction</th>
              {campaignId && <th>Outcome</th>}
              <th>From</th>
              <th>To</th>
              <th>Duration</th>
              <th>Preview</th>
            </tr>
          </Thead>
          <Tbody>
            {loading ? (
              <SkeletonRows cols={campaignId ? 7 : 6} />
            ) : data.results.length === 0 ? (
              <tr><td colSpan={campaignId ? 7 : 6}>
                <EmptyState
                  icon={<MessageSquare size={40} />}
                  title="No conversations yet"
                  body="Conversations appear here after your first call."
                />
              </td></tr>
            ) : data.results.map((c) => {
              const isLast = c.call_id === lastViewed;
              const to = `/conversations/${c.call_id}`;
              // Row-level click delegation: clicks anywhere on the row navigate,
              // while the single real link (date cell) keeps clean keyboard and
              // screen-reader semantics. A click on the link itself is left to the
              // link; a cmd/ctrl/middle click elsewhere opens a new tab using the
              // link's own resolved href (so no base path is hard-coded here).
              const onRow = (e: MouseEvent<HTMLTableRowElement>) => {
                if ((e.target as HTMLElement).closest('a')) return;
                const anchor = e.currentTarget.querySelector<HTMLAnchorElement>('a');
                if ((e.metaKey || e.ctrlKey) && anchor) {
                  window.open(anchor.href, '_blank', 'noopener,noreferrer');
                  return;
                }
                recordViewed(e, c.call_id);
                navigate(to);
              };
              const onRowAux = (e: MouseEvent<HTMLTableRowElement>) => {
                if (e.button !== 1 || (e.target as HTMLElement).closest('a')) return;
                const anchor = e.currentTarget.querySelector<HTMLAnchorElement>('a');
                if (anchor) window.open(anchor.href, '_blank', 'noopener,noreferrer');
              };
              const fromTo = formatFromTo(c);
              return (
              <tr
                key={c.call_id}
                ref={isLast ? lastViewedRowRef : undefined}
                onClick={onRow}
                onAuxClick={onRowAux}
                // Highlight the conversation you just came back from. No
                // position:relative here — the row navigates via a real link plus
                // the delegation above, not an absolutely-positioned overlay.
                style={isLast ? { background: 'var(--accent-cyan-glow)', boxShadow: 'inset 3px 0 0 var(--accent-cyan)' } : undefined}
              >
                <td style={{ padding: 0 }}>
                  <CellLink to={to} onClick={(e) => recordViewed(e, c.call_id)} style={{ fontSize: 13 }}
                    aria-label={`Open conversation from ${fromTo.from} to ${fromTo.to}`}>
                    {formatTs(c.ts)}
                  </CellLink>
                </td>
                <td><Badge variant={c.direction}>{c.direction}</Badge></td>
                {campaignId && (
                  <td>
                    {c.attempt_status
                      ? <Badge variant={outcomeBadgeVariant(c.attempt_status)}>{formatOutcome(c.attempt_status)}</Badge>
                      : '—'}
                  </td>
                )}
                <EndpointCell title={fromTo.from}>{fromTo.from}</EndpointCell>
                <EndpointCell title={fromTo.to}>{fromTo.to}</EndpointCell>
                <td>{formatDuration(c.duration_sec)}</td>
                <td style={{ color: 'var(--text-muted)', fontSize: 12, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {c.snippet || '—'}
                </td>
              </tr>
              );
            })}
          </Tbody>
        </Table>
      </Card>

      {!loading && (pageUrls.length > 0 || data.next_url) && (
        <CursorPagination
          page={pageUrls.length + 1}
          hasPrev={pageUrls.length > 0}
          hasNext={data.next_url !== null}
          onPrev={() => setPageUrls(pageUrls.slice(0, -1))}
          onNext={() => data.next_url && setPageUrls([...pageUrls, data.next_url])}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Audio Player
// ---------------------------------------------------------------------------

const PlayerBar = styled.div({
  position: 'sticky',
  top: -32,
  zIndex: 10,
  background: 'var(--bg-surface)',
  border: '1px solid var(--bg-border)',
  borderRadius: 8,
  padding: '12px 16px',
  display: 'flex',
  alignItems: 'center',
  gap: 14,
  marginBottom: 16,
  boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
});

const ScrubBar = styled.input({
  flex: 1,
  appearance: 'none',
  height: 4,
  borderRadius: 2,
  background: 'var(--bg-border)',
  outline: 'none',
  cursor: 'pointer',
  '&::-webkit-slider-thumb': {
    appearance: 'none',
    width: 14,
    height: 14,
    borderRadius: '50%',
    background: 'var(--accent-cyan)',
    cursor: 'pointer',
  },
});

const TimeLabel = styled.span({
  fontSize: 12,
  fontFamily: 'JetBrains Mono, monospace',
  color: 'var(--text-secondary)',
  minWidth: 40,
});

const PlayBtn = styled.button({
  background: 'var(--accent-cyan)',
  border: 'none',
  borderRadius: '50%',
  width: 36,
  height: 36,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  color: '#000',
  flexShrink: 0,
  transition: 'opacity 150ms',
  '&:hover': { opacity: 0.85 },
});

const DownloadBtn = styled.a({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--text-secondary)',
  flexShrink: 0,
  transition: 'color 150ms',
  '&:hover': { color: 'var(--text-primary)' },
});

function formatTime(s: number): string {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, '0')}`;
}

interface AudioPlayerProps {
  callId: string;
  currentTime: number;
  onTimeUpdate: (t: number) => void;
  onSeek: (seekFn: (t: number) => void) => void;
}

function AudioPlayer({ callId, onTimeUpdate, onSeek }: AudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    onSeek((t) => {
      if (audioRef.current) {
        audioRef.current.currentTime = t;
        audioRef.current.play().then(() => setPlaying(true)).catch(() => {});
      }
    });
  }, [onSeek]);

  const togglePlay = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else { a.play().then(() => setPlaying(true)).catch(() => {}); }
  };

  return (
    <PlayerBar>
      <audio
        ref={audioRef}
        src={`/dashboard/api/conversations/${callId}/recording`}
        onTimeUpdate={() => {
          if (!dragging) {
            const t = audioRef.current?.currentTime ?? 0;
            setCurrent(t);
            onTimeUpdate(t);
          }
        }}
        onLoadedMetadata={() => setDuration(audioRef.current?.duration ?? 0)}
        onEnded={() => setPlaying(false)}
      />
      <PlayBtn onClick={togglePlay}>
        {playing ? <Pause size={16} /> : <Play size={16} />}
      </PlayBtn>
      <TimeLabel>{formatTime(current)}</TimeLabel>
      <ScrubBar
        type="range"
        min={0}
        max={duration || 1}
        step={0.1}
        value={current}
        onMouseDown={() => setDragging(true)}
        onChange={(e) => {
          const t = parseFloat(e.target.value);
          setCurrent(t);
          if (audioRef.current) audioRef.current.currentTime = t;
        }}
        onMouseUp={() => setDragging(false)}
      />
      <TimeLabel>{formatTime(duration)}</TimeLabel>
      <DownloadBtn href={`/dashboard/api/conversations/${callId}/recording`} download>
        <Download size={18} />
      </DownloadBtn>
    </PlayerBar>
  );
}

// ---------------------------------------------------------------------------
// Transcript panel
// ---------------------------------------------------------------------------

const TERMINATION_LABELS: Record<string, (t: TerminationInfo) => string> = {
  'user-hangup': () => 'The user hung up.',
  'bot-hangup': () => 'The agent ended the call.',
  'bot-transfer': (t) => t.transfer_target ? `The call was transferred to ${t.transfer_target}.` : 'The call was transferred.',
  'voicemail': () => 'Voicemail was reached.',
  'bot-failure': () => 'The call ended due to an error.',
};

const TerminationRow = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '10px 12px',
  borderTop: '1px solid var(--bg-border)',
  color: 'var(--text-muted)',
  fontSize: 13,
});

const TurnRow = styled.div<{ active?: boolean }>(({ active }) => ({
  display: 'flex',
  gap: 12,
  padding: '10px 12px',
  borderLeft: active ? '2px solid var(--accent-cyan)' : '2px solid transparent',
  background: active ? 'var(--accent-cyan-glow)' : 'transparent',
  transition: 'border-color 120ms ease, background 120ms ease',
  borderRadius: '0 4px 4px 0',
  cursor: 'pointer',
  '&:hover': { background: 'var(--bg-surface-hover)' },
}));

const SpeakerLabel = styled.span<{ speaker?: string }>(({ speaker }) => ({
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: 11,
  fontWeight: 600,
  textTransform: 'uppercase',
  width: 50,
  flexShrink: 0,
  color: speaker === 'AGENT' ? 'var(--accent-cyan)' : 'var(--accent-amber)',
}));

const TurnTimestamp = styled.span({
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: 12,
  color: 'var(--text-muted)',
  width: 36,
  flexShrink: 0,
});

const TurnText = styled.span({
  fontSize: 14,
  color: 'var(--text-primary)',
  lineHeight: 1.5,
});

// ---------------------------------------------------------------------------
// Conversation detail
// ---------------------------------------------------------------------------

export function ConversationDetailPage() {
  const { callId } = useParams<{ callId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: me } = useFetchData<{ user_email: string }>('/dashboard/api/me', { user_email: '' });
  // Internal Guava/Gridspace users can open a call in the Monitor view.
  const showMonitor = canViewMonitor(me.user_email);
  const { data: conv, loading: convLoading } = useFetchData<ConversationDetail | null>(
    `/dashboard/api/conversations/${callId}`, null
  );
  const { data: transcript, loading: txLoading } = useFetchData<TranscriptTurn[]>(
    `/dashboard/api/conversations/${callId}/transcript`, []
  );

  const [currentTime, setCurrentTime] = useState(0);
  const seekFnRef = useRef<(t: number) => void>(() => {});
  const activeTurnRef = useRef<HTMLDivElement>(null);

  const activeTurnIdx = transcript.reduce((best, turn, i) => {
    return turn.offset_ms / 1000 <= currentTime ? i : best;
  }, -1);

  // biome-ignore lint/correctness/useExhaustiveDependencies: activeTurnIdx triggers the scroll when the active turn changes
  useEffect(() => {
    activeTurnRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [activeTurnIdx]);

  function handleDownload() {
    const blob = new Blob([JSON.stringify(transcript, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `transcript-${callId}.json`; a.click();
    URL.revokeObjectURL(url);
  }

  if (convLoading) return <div style={{ color: 'var(--text-secondary)', padding: 32 }}>Loading…</div>;
  if (!conv) return <ResourceNotFound label="Conversation" backTo="/conversations" />;
  const fromTo = formatFromTo(conv);

  return (
    <div>
      <PageHeader>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            // Plain history-back when we came from the list; but a link opened
            // directly (pasted URL / new tab) has no in-app history, so `-1`
            // would leave the app — fall back to the Conversations list instead.
            onClick={() => (location.key === 'default' ? navigate('/conversations') : navigate(-1))}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', display: 'flex' }}
          >
            <ArrowLeft size={18} />
          </button>
          <PageTitle>{formatTs(conv.ts)}</PageTitle>
          <Badge variant={conv.direction}>{conv.direction}</Badge>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {showMonitor && (
            <a
              href={`/monitor/calls/${callId}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ border: '1px solid var(--bg-border)', borderRadius: 6, padding: '7px 14px', cursor: 'pointer', color: 'var(--text-secondary)', display: 'flex', gap: 6, alignItems: 'center', fontSize: 14, textDecoration: 'none' }}
            >
              Open in Monitor <ExternalLink size={14} />
            </a>
          )}
          <button
            onClick={handleDownload}
            style={{ background: 'none', border: '1px solid var(--bg-border)', borderRadius: 6, padding: '7px 14px', cursor: 'pointer', color: 'var(--text-secondary)', display: 'flex', gap: 6, alignItems: 'center', fontSize: 14 }}
          >
            <Download size={14} /> Download Transcript
          </button>
        </div>
      </PageHeader>

      <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        <Muted>From: <span style={{ color: 'var(--text-primary)', fontFamily: 'monospace' }}>{fromTo.from}</span></Muted>
        <Muted>To: <span style={{ color: 'var(--text-primary)', fontFamily: 'monospace' }}>{fromTo.to}</span></Muted>
        <Muted>Duration: <span style={{ color: 'var(--text-primary)' }}>{formatDuration(conv.duration_sec)}</span></Muted>
      </div>

      <AudioPlayer
        callId={callId!}
        currentTime={currentTime}
        onTimeUpdate={setCurrentTime}
        onSeek={(fn) => { seekFnRef.current = fn; }}
      />

      <Card style={{ padding: 0, overflow: 'hidden' }}>
        {txLoading ? (
          <div style={{ padding: 24, color: 'var(--text-muted)' }}>Loading transcript…</div>
        ) : transcript.length === 0 ? (
          <div style={{ padding: 24, color: 'var(--text-muted)' }}>No transcript available yet.</div>
        ) : (
          <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
            {transcript.map((turn, i) => (
              <TurnRow
                key={turn.offset_ms}
                active={i === activeTurnIdx}
                ref={i === activeTurnIdx ? activeTurnRef : undefined}
                onClick={() => seekFnRef.current(turn.offset_ms / 1000)}
              >
                <SpeakerLabel speaker={turn.speaker.toUpperCase()}>{turn.speaker}</SpeakerLabel>
                <TurnTimestamp>{formatTime(turn.offset_ms / 1000)}</TurnTimestamp>
                <TurnText>{turn.text}</TurnText>
              </TurnRow>
            ))}
            {conv.termination && TERMINATION_LABELS[conv.termination.termination_reason] && (
              <TerminationRow>
                {TERMINATION_LABELS[conv.termination.termination_reason](conv.termination)}
              </TerminationRow>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

export function ConversationsPage() {
  return (
    <div>
      <PageHeader>
        <PageTitle>Conversations</PageTitle>
      </PageHeader>
      <ConversationsList />
    </div>
  );
}

export { ConversationsList };
